const OPTION = "[data-dropdown-option]";

class SubscriptionFrequency extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector("[data-dropdown-trigger]");
    this.panel = this.querySelector("[data-dropdown-panel]");
    this.valueEl = this.querySelector("[data-trigger-value]");
    this.spinner = this.querySelector("[data-spinner]");
    this.live = this.querySelector("[data-live]");
    this.options = Array.from(this.querySelectorAll(OPTION));

    if (!this.trigger || !this.panel || !this.options.length) return;

    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onPanelClick = this._onPanelClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDocumentPointer = this._onDocumentPointer.bind(this);

    this.trigger.addEventListener("click", this._onTriggerClick);
    this.panel.addEventListener("click", this._onPanelClick);
    this.addEventListener("keydown", this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("pointerdown", this._onDocumentPointer);
  }

  get isOpen() {
    return this.trigger.getAttribute("aria-expanded") === "true";
  }

  get selectedOption() {
    return this.options.find(
      (option) => option.getAttribute("aria-selected") === "true",
    );
  }

  open() {
    if (this.isOpen) return;
    this.trigger.setAttribute("aria-expanded", "true");
    this.panel.hidden = false;
    document.addEventListener("pointerdown", this._onDocumentPointer);
    (this.selectedOption || this.options[0]).focus();
  }

  close({ focusTrigger = false } = {}) {
    if (!this.isOpen) return;
    this.trigger.setAttribute("aria-expanded", "false");
    this.panel.hidden = true;
    document.removeEventListener("pointerdown", this._onDocumentPointer);
    if (focusTrigger) this.trigger.focus();
  }

  selectOption(option) {
    if (!option) return;
    if (option.getAttribute("aria-selected") === "true") {
      this.close({ focusTrigger: true });
      return;
    }
    this.valueEl.textContent = option.dataset.triggerLabel;
    this.close({ focusTrigger: true });
    this._change(option.dataset.sellingPlanId, option.dataset.triggerLabel);
  }

  _change(planId, label) {
    this._setLoading(true);
    if (this.live) this.live.textContent = label;
    window.changeCart({
      line: this.dataset.line,
      quantity: this.dataset.quantity,
      selling_plan: planId || null,
    });
    setTimeout(() => this._setLoading(false), 6000);
  }

  _setLoading(on) {
    if (this.spinner) this.spinner.hidden = !on;
    this.trigger.setAttribute("aria-busy", String(on));
    this.classList.toggle("subscription-frequency--loading", on);
  }

  _focusByOffset(offset) {
    const currentIndex = this.options.indexOf(document.activeElement);
    const lastIndex = this.options.length - 1;
    let nextIndex = currentIndex + offset;
    if (nextIndex < 0) nextIndex = lastIndex;
    if (nextIndex > lastIndex) nextIndex = 0;
    this.options[nextIndex].focus();
  }

  _onTriggerClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  _onPanelClick(event) {
    const option = event.target.closest(OPTION);
    if (option) this.selectOption(option);
  }

  _onDocumentPointer(event) {
    if (!this.contains(event.target)) this.close();
  }

  _onKeydown(event) {
    const onTrigger = event.target === this.trigger;
    const onOption = this.options.includes(event.target);

    switch (event.key) {
      case "ArrowDown":
        if (onTrigger && !this.isOpen) {
          event.preventDefault();
          this.open();
        } else if (onOption) {
          event.preventDefault();
          this._focusByOffset(1);
        }
        break;
      case "ArrowUp":
        if (onOption) {
          event.preventDefault();
          this._focusByOffset(-1);
        }
        break;
      case "Home":
        if (onOption) {
          event.preventDefault();
          this.options[0].focus();
        }
        break;
      case "End":
        if (onOption) {
          event.preventDefault();
          this.options[this.options.length - 1].focus();
        }
        break;
      case "Enter":
      case " ":
        if (onTrigger && !this.isOpen) {
          event.preventDefault();
          this.open();
        } else if (onOption) {
          event.preventDefault();
          this.selectOption(event.target);
        }
        break;
      case "Escape":
        if (this.isOpen) {
          event.preventDefault();
          this.close({ focusTrigger: true });
        }
        break;
      case "Tab":
        this.close();
        break;
    }
  }
}

if (!customElements.get("subscription-frequency")) {
  customElements.define("subscription-frequency", SubscriptionFrequency);
}
